import { initializeApp, getApps } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

/**
 * Firebase (Firestore + Auth + Storage). Configure via CRA env vars (see .env.example).
 * Safe no-op when unset: isFirebaseConfigured is false; existing API auth keeps working.
 */
const cfg = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const isFirebaseConfigured = Boolean(
  cfg.apiKey && cfg.authDomain && cfg.projectId && cfg.storageBucket && cfg.appId
);

/** Same as `REACT_APP_FIREBASE_PROJECT_ID` (for diagnostics / support). */
export const firebaseProjectId = cfg.projectId || "";

let app = null;
if (isFirebaseConfigured) {
  app = getApps().length ? getApps()[0] : initializeApp(cfg);
}

/** Use the exact bucket from env so rules apply to the same bucket as the Console. */
function getFirebaseStorage() {
  if (!app) return null;
  const b = cfg.storageBucket?.trim();
  if (!b) return getStorage(app);
  const gs = b.startsWith("gs://") ? b : `gs://${b}`;
  return getStorage(app, gs);
}

if (app && typeof window !== "undefined") {
  const siteKey = String(process.env.REACT_APP_APPCHECK_SITE_KEY || "").trim();
  const debug = process.env.REACT_APP_APPCHECK_DEBUG === "true";
  if (debug) {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  if (siteKey) {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("[Firebase] App Check init failed (duplicate init is OK after hot reload):", e);
      }
    }
  }
}

export const firebaseApp = app;
export const firebaseAuth = app ? getAuth(app) : null;
export const firestore = app ? getFirestore(app) : null;
export const firebaseStorage = getFirebaseStorage();
